import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ChevronRight, ChevronDown, Check, Zap, Monitor, Video, Layout,
  Smartphone, Building, Layers, Globe, BarChart3, RefreshCw, Settings, Users,
  Play, FileText, Target, DollarSign, Eye, Gauge, PieChart
} from "lucide-react";
import { API } from "../data/constants";
import { SEO } from "../components/SEO";

// ==================== DSP Overview ====================
export const DSPOverviewPage = () => {
  const navigate = useNavigate();

  const adFormats = [
    { icon: Monitor, title: "Display", desc: "Banner ads across premium websites" },
    { icon: Video, title: "Video", desc: "Pre-roll, mid-roll, and outstream video" },
    { icon: Layout, title: "Native", desc: "Seamlessly integrated content ads" },
    { icon: Smartphone, title: "Mobile / In-App", desc: "Reach users on mobile devices and apps" }
  ];

  const whyDsp = [
    { icon: Building, text: "White-label interface under FunAsia Digital branding" },
    { icon: Layers, text: "Manage all clients/campaigns from one dashboard" },
    { icon: Globe, text: "Access to 30+ traffic sources" },
    { icon: Zap, text: "Automated bidding + optimization tools" },
    { icon: BarChart3, text: "Reporting & analytics for performance visibility" },
    { icon: RefreshCw, text: "Bulk campaign management for scale" }
  ];

  return (
    <div className="pt-20" data-testid="dsp-overview-page">
      <SEO
        title="Programmatic DSP"
        description="Self-Serve Programmatic DSP for agencies & advertisers. White-label DSP powered by EPOM with display, video, native, and mobile inventory."
        path="/dsp"
      />

      <section className="section-padding bg-gradient-to-br from-gray-dark via-gray-900 to-gray-dark text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-2 text-orange-secondary font-semibold text-sm mb-4 bg-white/10 px-4 py-2 rounded-full">
              <Zap size={16} /> Programmatic Advertising
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Self-Serve Programmatic DSP for{" "}
              <span className="text-orange-secondary">Agencies & Advertisers</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Buy paid media at best rates with our white-label DSP powered by EPOM. Run campaigns
              across premium programmatic inventory with full transparency and control.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate("/dsp/get-access")} className="btn-primary text-lg" data-testid="dsp-hero-access-btn">
                Get DSP Access <ArrowRight size={20} className="inline ml-2" />
              </button>
              <button onClick={() => navigate("/dsp/how-it-works")} className="btn-secondary border-white text-white hover:bg-white/10 text-lg">
                How It Works <ChevronRight size={20} className="inline ml-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding" data-testid="dsp-formats-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">What You Can Buy</h2>
            <p className="text-gray-medium">Inventory sourced through multiple exchanges/traffic sources.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {adFormats.map((format, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl shadow-card text-center card-hover">
                <div className="w-16 h-16 rounded-xl bg-orange-light flex items-center justify-center mx-auto mb-4">
                  <format.icon className="text-orange-primary" size={32} />
                </div>
                <h3 className="font-display font-bold text-xl text-gray-dark mb-2">{format.title}</h3>
                <p className="text-gray-medium text-sm">{format.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-light" data-testid="dsp-why-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Why Our DSP</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyDsp.map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-card flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-light flex items-center justify-center flex-shrink-0">
                  <item.icon className="text-orange-primary" size={24} />
                </div>
                <p className="text-gray-dark font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding" data-testid="dsp-options-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Two Ways to Use It</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-card border-2 border-transparent hover:border-orange-primary transition-all card-hover">
              <div className="w-14 h-14 rounded-xl bg-orange-light flex items-center justify-center mb-6">
                <Settings className="text-orange-primary" size={28} />
              </div>
              <h3 className="font-display text-2xl font-bold text-gray-dark mb-3">Self-Serve DSP</h3>
              <p className="text-orange-primary font-medium mb-3">Client Operated</p>
              <p className="text-gray-medium">
                You control budgets, targeting, creatives, and optimization. Full access to the platform with your own login.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-card border-2 border-transparent hover:border-orange-primary transition-all card-hover">
              <div className="w-14 h-14 rounded-xl bg-orange-light flex items-center justify-center mb-6">
                <Users className="text-orange-primary" size={28} />
              </div>
              <h3 className="font-display text-2xl font-bold text-gray-dark mb-3">Managed DSP</h3>
              <p className="text-orange-primary font-medium mb-3">FunAsia Operated</p>
              <p className="text-gray-medium">
                We run everything for you—strategy, setup, optimization, reporting. Hands-off programmatic advertising.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding gradient-orange" data-testid="dsp-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">Ready to launch programmatic campaigns today?</h2>
          <button onClick={() => navigate("/dsp/get-access")} className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid="dsp-cta-access-btn">
            Request DSP Access <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};

// ==================== DSP How It Works ====================
export const DSPHowItWorksPage = () => {
  const navigate = useNavigate();

  const steps = [
    { num: "01", title: "Access & Onboarding", desc: "Get your white-label DSP portal credentials and complete platform onboarding with our team.", icon: Play },
    { num: "02", title: "Create Campaign", desc: "Set up your campaign with ad creatives, landing pages, and campaign objectives.", icon: FileText },
    { num: "03", title: "Targeting & Budget Control", desc: "Define your audience targeting, set budgets, and configure bidding strategies.", icon: Target },
    { num: "04", title: "Real-Time Buying (RTB) + Optimization", desc: "Your ads compete in real-time auctions. Our system optimizes bids automatically for best results.", icon: Zap },
    { num: "05", title: "Reporting & Insights", desc: "Access real-time dashboards with performance metrics, conversions, and actionable insights.", icon: BarChart3 }
  ];

  const capabilities = [
    { icon: Globe, title: "Audience Targeting", desc: "Geo, device, behavioral targeting where applicable" },
    { icon: DollarSign, title: "Bidding Rules", desc: "Automated bidding with optimization controls" },
    { icon: Eye, title: "Frequency Capping", desc: "Control ad exposure per user" },
    { icon: Gauge, title: "Budget Control", desc: "Daily and total budget management" },
    { icon: PieChart, title: "Real-Time Analytics", desc: "Live performance dashboards" },
    { icon: FileText, title: "Custom Reporting", desc: "Export and schedule reports" }
  ];

  return (
    <div className="pt-20" data-testid="dsp-how-it-works-page">
      <SEO title="DSP — How It Works" description="How our Programmatic DSP works — Access, Campaign Creation, Targeting, Real-Time Bidding, and Reporting." path="/dsp/how-it-works" />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-dark mb-4">How It Works</h1>
            <p className="text-gray-medium text-lg">
              Our DSP buys inventory programmatically via real-time bidding (RTB), supporting advanced targeting, optimization, and comprehensive reporting.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding" data-testid="dsp-steps-section">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {steps.map((step, idx) => (
              <div key={idx} className="bg-white p-6 md:p-8 rounded-2xl shadow-card flex flex-col md:flex-row items-start gap-6 card-hover">
                <div className="flex items-center gap-4">
                  <span className="text-5xl font-display font-bold text-orange-primary/20">{step.num}</span>
                  <div className="w-14 h-14 rounded-xl bg-orange-light flex items-center justify-center">
                    <step.icon className="text-orange-primary" size={28} />
                  </div>
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-gray-dark mb-2">{step.title}</h3>
                  <p className="text-gray-medium">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-light" data-testid="dsp-capabilities-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Platform Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-card card-hover">
                <cap.icon className="text-orange-primary mb-4" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{cap.title}</h3>
                <p className="text-gray-medium text-sm">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-dark mb-6">Ready to get started with programmatic?</h2>
          <button onClick={() => navigate("/dsp/get-access")} className="btn-primary text-lg" data-testid="dsp-hiw-access-btn">
            Get Access <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};

// ==================== DSP Pricing ====================
export const DSPPricingPage = () => {
  const navigate = useNavigate();

  const plans = [
    { name: "Self-Serve Starter", subtitle: "For businesses testing programmatic", features: ["DSP platform access", "Onboarding & setup support", "Email support", "Basic reporting", "Access to display & native inventory"], cta: "Request Access", action: () => navigate("/dsp/get-access"), popular: false },
    { name: "Self-Serve Pro", subtitle: "For multi-campaign advertisers", features: ["Everything in Starter", "Advanced reporting & analytics", "Priority support", "Multi-campaign management", "Video & mobile inventory access", "Dedicated onboarding specialist"], cta: "Request Access", action: () => navigate("/dsp/get-access"), popular: true },
    { name: "Managed DSP", subtitle: "Strategy + setup + ongoing optimization", features: ["Full campaign management", "Strategy development", "Creative guidance", "Monthly reporting cadence", "Dedicated account manager", "Performance optimization"], cta: "Book Strategy Call", action: () => navigate("/contact"), popular: false }
  ];

  return (
    <div className="pt-20" data-testid="dsp-pricing-page">
      <SEO title="DSP Pricing" description="DSP Pricing & Packages — Self-Serve Starter, Self-Serve Pro, and Managed DSP plans for agencies and advertisers." path="/dsp/pricing" />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-dark mb-4">DSP Pricing & Packages</h1>
            <p className="text-gray-medium text-lg">Choose the access level that fits your advertising needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, idx) => (
              <div key={idx} className={`relative bg-white rounded-2xl p-8 ${plan.popular ? "ring-2 ring-orange-primary shadow-orange-lg scale-105" : "shadow-card"} card-hover`} data-testid={`dsp-plan-${plan.name.toLowerCase().replace(/\s+/g, "-")}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="gradient-orange text-white text-sm font-semibold px-4 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                <h3 className="font-display text-xl font-bold text-gray-dark mb-1">{plan.name}</h3>
                <p className="text-orange-primary text-sm font-medium mb-6">{plan.subtitle}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-start gap-3 text-sm text-gray-dark">
                      <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button onClick={plan.action} className={`w-full py-3 rounded-full font-semibold transition-all ${plan.popular ? "btn-primary" : "btn-secondary"}`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-medium text-sm mt-8 max-w-2xl mx-auto">
            Final pricing depends on spend volume, inventory mix, and onboarding requirements. Contact us for a custom quote.
          </p>
        </div>
      </section>
    </div>
  );
};

// ==================== DSP FAQ ====================
export const DSPFAQPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const faqs = [
    { q: "What is a DSP?", a: "A Demand-Side Platform (DSP) is a technology platform that allows advertisers to buy digital ad inventory across multiple ad exchanges and networks through a single interface. It automates the process of purchasing display, video, mobile, and native ads programmatically." },
    { q: "How is DSP different from Google Ads or Meta Ads?", a: "While Google Ads and Meta Ads are self-contained platforms that only access their own inventory, a DSP connects to multiple ad exchanges and supply sources, giving you access to a much wider range of inventory across thousands of websites and apps. DSPs offer more advanced targeting and optimization capabilities." },
    { q: "What ad formats can I run?", a: "Our DSP supports Display (banner ads), Video (pre-roll, mid-roll, outstream), Native (in-feed, content recommendation), and Mobile/In-App advertising formats." },
    { q: "Do you offer white-label access?", a: "Yes! Our DSP is powered by EPOM and available as a white-label solution under FunAsia Digital branding. Agencies can manage multiple client campaigns from a single dashboard." },
    { q: "What reporting is available?", a: "You get access to real-time performance dashboards showing impressions, clicks, conversions, spend, CTR, and other key metrics. Custom reporting and scheduled exports are also available." },
    { q: "Can multiple client accounts be managed?", a: "Yes, the platform supports multi-account management, making it ideal for agencies handling campaigns for multiple clients from one interface." },
    { q: "What is RTB (Real-Time Bidding)?", a: "RTB is an auction-based system where ad impressions are bought and sold in real-time, typically in milliseconds. When a user visits a webpage, an auction occurs and the highest bidder's ad is displayed. This allows for efficient, targeted ad buying at scale." },
    { q: "Do you offer managed service?", a: "Yes! If you prefer a hands-off approach, our Managed DSP service handles everything—strategy, campaign setup, optimization, and reporting. We run your campaigns while you focus on your business." },
    { q: "What targeting options are available?", a: "Targeting options include geographic (country, region, city), device type, operating system, browser, time of day, and behavioral targeting where applicable. Advanced options depend on inventory sources." },
    { q: "How do I get started?", a: "Simply fill out our access request form and our team will contact you with onboarding steps and portal access within 1-2 business days." },
    { q: "Is there a minimum ad spend?", a: "Minimum spend requirements vary based on your selected plan. Contact us for specific details based on your advertising goals." },
    { q: "What payment methods do you accept?", a: "We accept credit cards, wire transfers, and can set up invoicing for larger accounts. Payment terms are discussed during onboarding." }
  ];

  return (
    <div className="pt-20" data-testid="dsp-faq-page">
      <SEO title="DSP FAQ" description="Frequently asked questions about FunAsia Digital's Programmatic DSP, real-time bidding, targeting, and reporting." path="/dsp/faq" />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-gray-dark mb-4">DSP FAQ</h1>
            <p className="text-gray-medium text-lg">Frequently asked questions about our Programmatic DSP</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl shadow-card overflow-hidden" data-testid={`faq-item-${idx}`}>
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors">
                  <span className="font-display font-semibold text-gray-dark">{faq.q}</span>
                  <ChevronDown size={20} className={`text-orange-primary flex-shrink-0 transition-transform ${openFaq === idx ? "rotate-180" : ""}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-medium leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-medium mb-4">Still have questions?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate("/contact")} className="btn-secondary">Contact Us</button>
              <button onClick={() => navigate("/dsp/get-access")} className="btn-primary">
                Request Access <ArrowRight size={18} className="inline ml-2" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ==================== DSP Get Access ====================
export const DSPGetAccessPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: "", company: "", email: "", phone: "", website: "", monthly_spend: "", primary_goal: "", markets: "", notes: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const spendRanges = ["Under $1,000/month", "$1,000 - $5,000/month", "$5,000 - $10,000/month", "$10,000 - $25,000/month", "$25,000 - $50,000/month", "$50,000+/month"];
  const goals = ["Lead Generation", "E-commerce Sales", "Brand Awareness", "App Installs", "Other"];

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await axios.post(`${API}/dsp-access`, formData);
      setSubmitStatus({ type: "success", message: response.data.message });
      setFormData({ name: "", company: "", email: "", phone: "", website: "", monthly_spend: "", primary_goal: "", markets: "", notes: "" });
    } catch (error) {
      setSubmitStatus({ type: "error", message: error.response?.data?.detail || "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="dsp-get-access-page">
      <SEO title="Request DSP Access" description="Request access to FunAsia Digital's white-label Programmatic DSP. EPOM-powered, 30+ traffic sources, dedicated onboarding." path="/dsp/get-access" />

      <section className="section-padding bg-gradient-to-br from-gray-dark via-gray-900 to-gray-dark text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 text-orange-secondary font-semibold text-sm mb-4">
                <Zap size={16} /> DSP Access Request
              </span>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-4">Request Access to FunAsia Digital DSP</h1>
              <p className="text-gray-300 text-lg mb-8">
                Get access to our EPOM-powered white-label DSP for self-serve media buying. Our team will contact you with onboarding steps and portal access.
              </p>

              <div className="space-y-4 mb-8">
                {["White-label platform access", "30+ traffic sources", "Display, Video, Native, Mobile formats", "Real-time reporting & analytics", "Dedicated onboarding support"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <Check size={20} className="text-orange-secondary" />
                    <span className="text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white/10 rounded-xl p-6">
                <p className="text-gray-300 mb-3">Prefer we manage it for you?</p>
                <button onClick={() => navigate("/contact")} className="btn-secondary border-white text-white hover:bg-white/10">
                  Talk to a DSP Specialist <ArrowRight size={18} className="inline ml-2" />
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 text-gray-dark">
              <h2 className="font-display text-2xl font-bold mb-6">Get Started</h2>
              <form onSubmit={handleSubmit} data-testid="dsp-access-form">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Name *</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-name-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Company *</label>
                      <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-company-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-email-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Phone *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-phone-input" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Website</label>
                    <input type="url" name="website" value={formData.website} onChange={handleChange} placeholder="https://" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-website-input" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Monthly Ad Spend Range *</label>
                      <select name="monthly_spend" value={formData.monthly_spend} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-spend-select">
                        <option value="">Select range</option>
                        {spendRanges.map((range, idx) => (<option key={idx} value={range}>{range}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Primary Goal *</label>
                      <select name="primary_goal" value={formData.primary_goal} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-goal-select">
                        <option value="">Select goal</option>
                        {goals.map((goal, idx) => (<option key={idx} value={goal}>{goal}</option>))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Target Markets (Geo)</label>
                    <input type="text" name="markets" value={formData.markets} onChange={handleChange} placeholder="e.g., USA, Canada, UK" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition" data-testid="dsp-markets-input" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Additional Notes</label>
                    <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} placeholder="Tell us about your advertising needs..." className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-orange-primary focus:border-transparent outline-none transition resize-none" data-testid="dsp-notes-input" />
                  </div>
                </div>

                {submitStatus && (
                  <div className={`mt-4 p-4 rounded-lg ${submitStatus.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="dsp-status-message">
                    {submitStatus.message}
                  </div>
                )}

                <button type="submit" disabled={isSubmitting} className="w-full btn-primary mt-6 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="dsp-submit-btn">
                  {isSubmitting ? "Submitting..." : "Request Access"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
